import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  Archive,
  ArchiveRestore,
  ArrowLeft,
  Building2,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  CreditCard,
  CalendarDays,
  GripVertical,
  Plus,
  Play,
  RefreshCw,
  Save,
  Search,
  Send,
  SkipForward,
  Square,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import api from '../api';
import ErrorAlert from '../components/ErrorAlert';
import ComposerSignalsTab, { composerSignalIssueCountFor } from './placeWorkbench/ComposerSignalsTab';
import { AddPlaceModal, ImportPlacesModal } from './placeWorkbench/PlaceWorkbenchGlobalActions';

import {
  PAGE_SIZE,
  NEW_CARD_KEY,
  DETAIL_PANEL_DEFAULT_WIDTH,
  VOCAB_FAMILIES,
  CARD_FIELDS,
  PLACE_FIELDS,
  FRESHNESS_RISK_OPTIONS,
  RETRIEVAL_LANE_OPTIONS,
  SAVED_VIEW_PRESETS,
  WORKER_COMMANDS,
  text,
  labelize,
  formatDate,
  readiness,
  stageLabel,
  stageBadgeKind,
  cardIdentity,
  cardInboxCount,
  getSavedView,
  cardMatchesSavedView,
  savedViewQueryParams,
  clampDetailPanelWidth,
  isEditableShortcutTarget,
  formFromCard,
  newCardDraft,
  formFromPlace,
  changedPayload,
  TextInput,
  TextArea,
  SelectField,
  MultiPicker,
  displayDiffValue,
  publishedProjectionDiffs,
  DiffRows,
  CardTags,
  Badge,
  queueFocusTab,
  queueFocusField,
  ReadinessIssueRow,
  Section,
  PlaceContactSourceEditor,
  WorkbenchTabs,
  ListRow,
} from './placeWorkbench/PlaceWorkbenchParts';
import {
  approvalReadiness,
  cardLifecycleLabel,
  isActiveProposalItem,
  latestEnrichmentRunKind,
  latestEnrichmentRunMessage,
  normalizedCardTitle,
  proposalTier,
  savedViewChipClass,
  stageBannerClass,
  stageBannerSentence,
  versionSnapshotEntries,
} from './placeWorkbench/placeWorkbenchViewUtils';

function VersionHistory({ versions, publishedVersionId }) {
  const [openVersionId, setOpenVersionId] = useState(versions[0]?.id || null);

  useEffect(() => {
    setOpenVersionId(current => versions.some(version => version.id === current) ? current : (versions[0]?.id || null));
  }, [versions]);

  return (
    <details open className="overflow-hidden rounded-lg border border-stone-200 bg-white text-sm">
      <summary className="cursor-pointer px-4 py-3 font-medium text-stone-800">Version history · immutable snapshots</summary>
      <div className="border-t border-stone-200">
        {versions.length === 0 && <p className="px-4 py-3 text-stone-500">No approved versions yet.</p>}
        {versions.map(version => {
          const isOpen = openVersionId === version.id;
          const isLive = version.id === publishedVersionId;
          const entries = versionSnapshotEntries(version.snapshot);
          return (
            <div key={version.id} className="border-t border-stone-200 first:border-t-0">
              <button
                type="button"
                onClick={() => setOpenVersionId(current => current === version.id ? null : version.id)}
                aria-expanded={isOpen}
                className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-stone-50"
              >
                {isOpen ? <ChevronDown className="h-4 w-4 shrink-0 text-stone-500" /> : <ChevronRight className="h-4 w-4 shrink-0 text-stone-500" />}
                <span className="font-semibold text-stone-900">v{version.version_number}</span>
                <Badge kind={isLive ? 'success' : 'neutral'}>{labelize(version.status)}</Badge>
                {isLive && <Badge kind="success">Live</Badge>}
                {!isLive && version.status === 'published' && <span className="text-xs text-stone-500">Previously live</span>}
                <span className="ml-auto hidden text-xs text-stone-500 sm:inline">Approved {formatDate(version.approved_at)} · Published {formatDate(version.published_at)}</span>
              </button>
              {isOpen && (
                <div className="border-t border-stone-200 bg-stone-50 px-4 py-4">
                  <div className="mb-4 flex flex-wrap gap-x-6 gap-y-1 text-xs text-stone-500">
                    <span>Approved by {version.approved_by || version.created_by || '-'}</span>
                    <span>Approved {formatDate(version.approved_at)}</span>
                    <span>Published by {version.published_by || '-'}</span>
                    <span>Published {formatDate(version.published_at)}</span>
                  </div>
                  {entries.length === 0 ? (
                    <p className="text-stone-500">No snapshot fields are stored for this version.</p>
                  ) : (
                    <dl className="grid gap-x-8 gap-y-3 md:grid-cols-2">
                      {entries.map(([field, value]) => (
                        <div key={field} className="min-w-0 border-b border-stone-200 pb-2">
                          <dt className="text-xs font-medium text-stone-500">{labelize(field)}</dt>
                          <dd className="mt-1 whitespace-pre-wrap break-words text-stone-900">{displayDiffValue(value)}</dd>
                        </div>
                      ))}
                    </dl>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </details>
  );
}

function formatEventDateTime(value) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function eventConfirmationSummary(event) {
  const confirmation = event?.owner_confirmation || {};
  return text(confirmation.confirmation_summary || confirmation.confirmed_by);
}

function DatedEventsSection({ events = [] }) {
  if (!events.length) return null;
  return (
    <Section eyebrow="Dated events" title={`Upcoming event${events.length === 1 ? '' : 's'}`}>
      <div className="grid gap-3">
        {events.map(event => (
          <div key={event.id || `${event.title}-${event.start_datetime_local}`} className="rounded-lg border border-stone-200 bg-stone-50 p-3">
            <div className="flex items-start gap-3">
              <span className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-stone-200 bg-white text-stone-600">
                <CalendarDays className="h-4 w-4" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="font-medium text-stone-900">{event.title || 'Dated event'}</p>
                  {event.date_status && <Badge kind="success">{labelize(event.date_status)}</Badge>}
                </div>
                <p className="mt-1 text-sm text-stone-700">
                  {formatEventDateTime(event.start_datetime_local)}
                  {event.end_datetime_local ? ` - ${formatEventDateTime(event.end_datetime_local)}` : ''}
                </p>
                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-stone-500">
                  {event.series && <span>Series: {event.series}</span>}
                  {event.artist && <span>Artist: {event.artist}</span>}
                  {event.capture_date && <span>Captured: {event.capture_date}</span>}
                </div>
                {event.source_url && (
                  <a
                    href={event.source_url}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-2 inline-flex text-xs font-medium text-blue-700 hover:text-blue-900"
                  >
                    {event.source_page_title || event.source_url}
                  </a>
                )}
                {eventConfirmationSummary(event) && (
                  <p className="mt-2 rounded-md border border-blue-100 bg-white px-2 py-1.5 text-xs leading-5 text-blue-900">
                    {eventConfirmationSummary(event)}
                  </p>
                )}
                {event.source_location_caveat && (
                  <p className="mt-2 text-xs leading-5 text-stone-500">{event.source_location_caveat}</p>
                )}
                {event.workshops_deferred && (
                  <p className="mt-2 text-xs text-stone-500">Workshop card remains deferred until source/location is corrected.</p>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
}

export default function PlaceWorkbenchView({ onGoToChanges, openTarget, returnToChanges, onReturnToChanges }) {
  const [cards, setCards] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [savedViews] = useState(SAVED_VIEW_PRESETS);
  const [savedView, setSavedView] = useState('all');
  const [savedViewCounts, setSavedViewCounts] = useState({});
  const [queueSession, setQueueSession] = useState(null);
  const [loadingList, setLoadingList] = useState(false);
  const [listLoaded, setListLoaded] = useState(false);
  const [countsError, setCountsError] = useState('');
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [error, setError] = useState('');
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailPanelWidth, setDetailPanelWidth] = useState(DETAIL_PANEL_DEFAULT_WIDTH);
  const [resizingDetailPanel, setResizingDetailPanel] = useState(false);
  const [detail, setDetail] = useState(null);
  const [place, setPlace] = useState(null);
  const [cardForm, setCardForm] = useState(formFromCard(null));
  const [placeForm, setPlaceForm] = useState(formFromPlace(null));
  const [focusField, setFocusField] = useState(null);
  const [activeTab, setActiveTab] = useState('card');
  const [selectedId, setSelectedId] = useState(null);
  const [selectedCardKey, setSelectedCardKey] = useState('primary');
  const [saving, setSaving] = useState(false);
  const [cardLifecycleAction, setCardLifecycleAction] = useState(null);
  const [savingPlaceRecord, setSavingPlaceRecord] = useState(null);
  const [creatingCard, setCreatingCard] = useState(false);
  const [draftReturnCardKey, setDraftReturnCardKey] = useState(null);
  const [showArchivedCards, setShowArchivedCards] = useState(false);
  const [showAddPlace, setShowAddPlace] = useState(false);
  const [showImportCsv, setShowImportCsv] = useState(false);
  const [mutatingCardKey, setMutatingCardKey] = useState(null);
  const [savedTarget, setSavedTarget] = useState(null);
  const [saveNotice, setSaveNotice] = useState('');
  const [publishConflict, setPublishConflict] = useState(null);
  const [capabilities, setCapabilities] = useState(null);
  const [actionsOpen, setActionsOpen] = useState(false);
  const [runningCommand, setRunningCommand] = useState(null);
  const [acceptingUnknownDimension, setAcceptingUnknownDimension] = useState(null);
  const [tags, setTags] = useState([]);
  const [vocab, setVocab] = useState({});
  const loadSeq = useRef(0);
  const countsSeq = useRef(0);
  const detailSeq = useRef(0);
  const taxonomyLoaded = useRef(false);
  const taxonomyLoading = useRef(null);
  const saveNoticeTimer = useRef(null);
  const lastOpenTargetKey = useRef('');
  const activeSavedView = useMemo(() => getSavedView(savedViews, savedView), [savedViews, savedView]);

  const showSaveNotice = useCallback((message, target = null) => {
    if (saveNoticeTimer.current) window.clearTimeout(saveNoticeTimer.current);
    setSaveNotice(message);
    setSavedTarget(target);
    saveNoticeTimer.current = window.setTimeout(() => {
      setSaveNotice('');
      setSavedTarget(null);
      saveNoticeTimer.current = null;
    }, 1800);
  }, []);

  useEffect(() => () => {
    if (saveNoticeTimer.current) window.clearTimeout(saveNoticeTimer.current);
  }, []);

  const loadTaxonomy = useCallback(async () => {
    if (taxonomyLoaded.current) return;
    if (taxonomyLoading.current) return taxonomyLoading.current;
    taxonomyLoading.current = (async () => {
      const tagData = await api.getTags().catch(() => []);
      setTags(tagData);
      const loaded = await Promise.allSettled(
        VOCAB_FAMILIES.map(async family => [family, await api.getVocabularyTerms(family, 'approved')])
      );
      const nextVocab = {};
      loaded.forEach(result => {
        if (result.status !== 'fulfilled') return;
        const [family, data] = result.value;
        nextVocab[family] = data;
      });
      setVocab(current => ({ ...current, ...nextVocab }));
      if (tagData.length || Object.keys(nextVocab).length) {
        taxonomyLoaded.current = true;
      }
    })().finally(() => {
      taxonomyLoading.current = null;
    });
    return taxonomyLoading.current;
  }, []);

  useEffect(() => {
    api.getAdminCapabilities()
      .then(setCapabilities)
      .catch(() => setCapabilities({ worker_commands: {} }));
  }, []);

  const loadList = useCallback(async ({ force = false } = {}) => {
    if (!force && !search && activeSavedView.id === 'all') {
      setCards([]);
      setListLoaded(false);
      setLoadingList(false);
      return;
    }
    const seq = loadSeq.current + 1;
    loadSeq.current = seq;
    setLoadingList(true);
    setListLoaded(true);
    try {
      const params = { limit: PAGE_SIZE, offset: page * PAGE_SIZE, ...savedViewQueryParams(activeSavedView) };
      if (search) params.search = search;
      const response = await api.getPlaceCards(params);
      if (seq !== loadSeq.current) return;
      const items = response.items || response || [];
      const nextTotal = response.total || items.length;
      setCards(items);
      setTotal(nextTotal);
      setSavedViewCounts(current => ({ ...current, [activeSavedView.id]: nextTotal }));
      setError('');
    } catch (e) {
      if (seq !== loadSeq.current) return;
      setError(e.message);
    } finally {
      if (seq === loadSeq.current) setLoadingList(false);
    }
  }, [activeSavedView, page, search]);

  const loadSavedViewCounts = useCallback(async () => {
    const seq = countsSeq.current + 1;
    countsSeq.current = seq;
    const params = {};
    if (search) params.search = search;
    try {
      const response = await api.getPlaceCardStageCounts(params);
      if (seq !== countsSeq.current) return;
      setSavedViewCounts(response.counts || {});
      setCountsError('');
    } catch (error) {
      if (seq !== countsSeq.current) return;
      setCountsError(error.message);
    }
  }, [search]);

  useEffect(() => {
    if (search || activeSavedView.id !== 'all') {
      loadList();
      return;
    }
    setCards([]);
    setListLoaded(false);
    setLoadingList(false);
  }, [activeSavedView.id, loadList, search]);

  useEffect(() => {
    loadSavedViewCounts();
  }, [loadSavedViewCounts]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(0);
      setSearch(searchInput.trim());
    }, 250);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const loadDetail = useCallback(async (placeMasterId, cardKey = 'primary', tab = activeTab) => {
    if (!placeMasterId) return;
    const seq = detailSeq.current + 1;
    detailSeq.current = seq;
    setDetailOpen(true);
    setLoadingDetail(true);
    setError('');
    try {
      const cardData = await api.getWorkbenchCard(placeMasterId, cardKey);
      if (seq !== detailSeq.current) return;
      setCreatingCard(false);
      setDraftReturnCardKey(null);
      setSelectedId(placeMasterId);
      setSelectedCardKey(cardData.card_key || cardKey);
      setDetail(cardData);
      setCardForm(formFromCard(cardData));
      setPlace(null);
      setPlaceForm(formFromPlace(null));
      setPublishConflict(null);
      setActiveTab(tab);
      setLoadingDetail(false);
      loadTaxonomy().catch(() => {});
      try {
        const placeData = await api.getPlace(placeMasterId);
        if (seq !== detailSeq.current) return;
        setPlace(placeData);
        setPlaceForm(formFromPlace(placeData));
      } catch (placeError) {
        if (seq !== detailSeq.current) return;
        setError(`Card opened, but place metadata failed to load: ${placeError.message}`);
      }
    } catch (e) {
      if (seq !== detailSeq.current) return;
      setError(e.message);
    } finally {
      if (seq === detailSeq.current) setLoadingDetail(false);
    }
  }, [activeTab, loadTaxonomy]);

  useEffect(() => {
    if (!openTarget?.placeId) return;
    const targetKey = `${openTarget.placeId}:${openTarget.cardKey || 'primary'}:${openTarget.openedAt || ''}`;
    if (lastOpenTargetKey.current === targetKey) return;
    lastOpenTargetKey.current = targetKey;
    loadDetail(openTarget.placeId, openTarget.cardKey || 'primary', 'card');
  }, [loadDetail, openTarget]);

  useEffect(() => {
    if (!focusField || loadingDetail) return;
    const timer = setTimeout(() => {
      const node = document.querySelector(`[data-focus="${focusField}"]`);
      if (!node) return;
      node.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const focusable = node.matches?.('button,input,textarea,select')
        ? node
        : node.querySelector?.('button,input,textarea,select');
      focusable?.focus?.({ preventScroll: true });
    }, 120);
    return () => clearTimeout(timer);
  }, [focusField, loadingDetail, activeTab]);

  useEffect(() => {
    if (!resizingDetailPanel) return undefined;
    const previousCursor = document.body.style.cursor;
    const previousUserSelect = document.body.style.userSelect;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    const onMouseMove = event => {
      setDetailPanelWidth(clampDetailPanelWidth(window.innerWidth - event.clientX));
    };
    const onMouseUp = () => setResizingDetailPanel(false);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      document.body.style.cursor = previousCursor;
      document.body.style.userSelect = previousUserSelect;
    };
  }, [resizingDetailPanel]);

  const currentState = readiness(detail);
  const reviewState = approvalReadiness(detail);
  const readyForApproval = reviewState.issueCount === 0;
  const publishedCardLocked = detail?.publish_status === 'published';
  const cardHasUnsavedChanges = Boolean(
    detail && (creatingCard || Object.keys(changedPayload(cardForm, detail, CARD_FIELDS)).length > 0)
  );
  const latestApprovedVersion = (detail?.versions || []).find(version => ['approved', 'published'].includes(version.status)) || null;
  const hasCurrentApproval = Boolean(
    !cardHasUnsavedChanges
    && latestApprovedVersion
    && ['approved', 'published'].includes(detail?.publish_status)
  );
  const canApproveCard = Boolean(
    detail
    && !detail.is_archived
    && !detail.is_unsaved
    && !cardHasUnsavedChanges
    && readyForApproval
    && !hasCurrentApproval
  );
  const canPublishCard = Boolean(
    hasCurrentApproval
    && detail?.publish_status === 'approved'
    && currentState.publishable
  );
  const filteredCards = useMemo(() => {
    return cards.filter(card => cardMatchesSavedView(card, activeSavedView));
  }, [activeSavedView, cards]);
  const displayedTotal = listLoaded ? total : (savedViewCounts[activeSavedView.id] ?? (countsError ? '-' : '...'));
  const savedViewCountLabel = useCallback((view) => {
    if (Object.prototype.hasOwnProperty.call(savedViewCounts, view.id)) {
      return savedViewCounts[view.id];
    }
    if (listLoaded && savedView === view.id) return total;
    return countsError ? '-' : '...';
  }, [countsError, listLoaded, savedView, savedViewCounts, total]);
  const activeInboxRequests = useMemo(
    () => (detail?.change_requests || []).filter(request => ['pending', 'needs_followup'].includes(request.overall_status)),
    [detail]
  );
  const activeSignalProposals = useMemo(
    () => (detail?.signal_proposals || []).filter(signal => signal.status === 'proposed'),
    [detail]
  );
  const activeWarningProposals = useMemo(
    () => (detail?.warning_proposals || []).filter(warning => warning.status === 'proposed'),
    [detail]
  );
  const activeProposalItems = useMemo(() => {
    if (Array.isArray(detail?.proposal_items)) {
      return detail.proposal_items.filter(isActiveProposalItem);
    }
    const legacyItems = [
      ...activeWarningProposals.map(warning => ({
        ...warning,
        proposal_item_id: warning.proposal_item_id || warning.id,
        item_type: 'warning',
        source_item_id: warning.id,
        payload: warning,
      })),
      ...activeSignalProposals.map(signal => ({
        ...signal,
        id: signal.proposal_item_id || `signal:${signal.id}`,
        source_item_id: signal.id,
        item_type: 'signal',
        tier: 'suggestion',
        scope: 'place',
        target_label: signal.display_text,
        blocking: false,
        source: { type: signal.source_type },
        payload: signal,
      })),
      ...activeInboxRequests.flatMap(request => {
        if (request.request_kind === 'new_listing') {
          return [{
            ...request,
            id: request.proposal_item_id || `change_request:${request.id}`,
            source_item_id: request.id,
            item_type: 'new_listing',
            tier: 'suggestion',
            scope: 'place',
            target_label: request.title,
            blocking: false,
            source: { type: request.source_type },
            payload: request,
          }];
        }
        return (request.fields || []).filter(field => ['pending', 'needs_followup'].includes(field.field_status)).map(field => ({
          ...field,
          id: field.proposal_item_id || `field:${request.id}:${field.id}`,
          source_item_id: field.id,
          item_type: 'field_update',
          tier: 'correction',
          scope: field.scope || 'place',
          target_label: field.target_label,
          blocking: false,
          source: { type: request.source_type },
          payload: field,
        }));
      }),
    ];
    return legacyItems.filter(isActiveProposalItem);
  }, [detail, activeInboxRequests, activeSignalProposals, activeWarningProposals]);
  const blockingProposalCount = useMemo(
    () => activeProposalItems.filter(item => proposalTier(item) === 'blocking').length,
    [activeProposalItems]
  );
  const correctionProposalCount = useMemo(
    () => activeProposalItems.filter(item => proposalTier(item) === 'correction').length,
    [activeProposalItems]
  );
  const publishedDiffs = useMemo(
    () => publishedProjectionDiffs(cardForm, detail?.published_item),
    [cardForm, detail]
  );
  const approvedMatchSignals = useMemo(
    () => detail?.approved_match_signals || [],
    [detail]
  );
  const pendingMatchSignals = useMemo(
    () => activeSignalProposals.filter(signal => signal.dimension || signal.signal_family),
    [activeSignalProposals]
  );
  const acceptedUnknownMatchSignals = useMemo(
    () => detail?.accepted_unknown_match_signals || [],
    [detail]
  );
  const composerSignalIssueCount = useMemo(
    () => composerSignalIssueCountFor(cardForm, approvedMatchSignals, pendingMatchSignals, acceptedUnknownMatchSignals),
    [cardForm, approvedMatchSignals, pendingMatchSignals, acceptedUnknownMatchSignals]
  );
  const enrichCardCommand = useMemo(
    () => WORKER_COMMANDS.find(command => command.id === 'enrich_card'),
    []
  );

  const saveCard = async ({ manageSaving = true, showFeedback = true } = {}) => {
    if (!selectedId || !selectedCardKey || !detail) return detail;
    if (creatingCard && selectedCardKey === NEW_CARD_KEY) {
      return createCard({ manageSaving, showFeedback });
    }
    if (detail.is_archived) {
      setError('Restore the archived card before changing it.');
      return detail;
    }
    if (publishedCardLocked) {
      setError('Published cards are read-only. Archive and restore this card before editing it.');
      return detail;
    }
    const payload = changedPayload(cardForm, detail, CARD_FIELDS);
    if (Object.prototype.hasOwnProperty.call(payload, 'card_title')) {
      const nextTitle = text(payload.card_title);
      if (!nextTitle) {
        setError('Card title is required.');
        return detail;
      }
      const normalizedTitle = normalizedCardTitle(nextTitle);
      const conflict = (detail.cards || place?.cards || []).some(card => (
        card.card_key !== selectedCardKey && normalizedCardTitle(card.card_title) === normalizedTitle
      ));
      if (conflict) {
        setError(`Card title '${nextTitle}' already exists for this place.`);
        return detail;
      }
    }
    if (Object.keys(payload).length === 0) {
      if (showFeedback) showSaveNotice('Card is up to date.', 'card');
      return detail;
    }
    if (manageSaving) setSaving(true);
    setSavedTarget(null);
    try {
      const updated = await api.patchWorkbenchCard(selectedId, selectedCardKey, payload);
      setDetail(updated);
      setCardForm(formFromCard(updated));
      setPublishConflict(null);
      setSelectedCardKey(updated.card_key || selectedCardKey);
      await loadList();
      if (showFeedback) showSaveNotice('Card saved.', 'card');
      return updated;
    } catch (e) {
      setError(e.message);
      throw e;
    } finally {
      if (manageSaving) setSaving(false);
    }
  };

  const savePlace = async () => {
    if (!selectedId || !place) return place;
    const payload = changedPayload(placeForm, place, PLACE_FIELDS);
    if (Object.keys(payload).length === 0) {
      showSaveNotice('Place is up to date.', 'place');
      return place;
    }
    setSaving(true);
    setSavedTarget(null);
    try {
      const updated = await api.patchPlace(selectedId, payload);
      setPlace(updated);
      setPlaceForm(formFromPlace(updated));
      await loadDetail(selectedId, selectedCardKey, 'place');
      await loadList();
      showSaveNotice('Place saved.', 'place');
      return updated;
    } catch (e) {
      setError(e.message);
      throw e;
    } finally {
      setSaving(false);
    }
  };

  const applyPlaceRecordMutation = async (key, request, successMessage) => {
    setSavingPlaceRecord(key);
    setError('');
    try {
      const updated = await request();
      setPlace(updated);
      setPlaceForm(formFromPlace(updated));
      setDetail(current => current ? {
        ...current,
        place_name: updated.name || updated.place_name,
        phone: updated.phone,
        email: updated.email,
        website_url: updated.website_url,
        source_url: updated.source_url,
        social_links: updated.social_links || [],
      } : current);
      await loadList();
      showSaveNotice(successMessage);
      return updated;
    } catch (e) {
      setError(e.message);
      throw e;
    } finally {
      setSavingPlaceRecord(null);
    }
  };

  const createPlaceContact = body => applyPlaceRecordMutation(
    'contact:new',
    () => api.createPlaceContact(selectedId, body),
    'Contact added.'
  );
  const updatePlaceContact = (contactId, body) => applyPlaceRecordMutation(
    `contact:${contactId}`,
    () => api.patchPlaceContact(selectedId, contactId, body),
    'Contact saved.'
  );
  const deletePlaceContact = contactId => applyPlaceRecordMutation(
    `contact:${contactId}`,
    () => api.deletePlaceContact(selectedId, contactId),
    'Contact removed.'
  );
  const createPlaceSource = body => applyPlaceRecordMutation(
    'source:new',
    () => api.createPlaceSource(selectedId, body),
    'Source added.'
  );
  const updatePlaceSource = (sourceId, body) => applyPlaceRecordMutation(
    `source:${sourceId}`,
    () => api.patchPlaceSource(selectedId, sourceId, body),
    'Source saved.'
  );
  const deletePlaceSource = sourceId => applyPlaceRecordMutation(
    `source:${sourceId}`,
    () => api.deletePlaceSource(selectedId, sourceId),
    'Source removed.'
  );
  const createManualSourceSnapshot = async body => {
    if (!selectedId || !selectedCardKey) return null;
    setSavingPlaceRecord('manual-source-snapshot');
    setError('');
    try {
      const snapshot = await api.createManualSourceSnapshot(selectedId, selectedCardKey, body);
      await loadDetail(selectedId, selectedCardKey, activeTab);
      await loadList();
      showSaveNotice(`Manual source snapshot ${snapshot.id} captured. You can run Enrich card now.`);
      return snapshot;
    } catch (e) {
      setError(e.message);
      throw e;
    } finally {
      setSavingPlaceRecord(null);
    }
  };

  const approveCard = async () => {
    if (!selectedId || !selectedCardKey || !canApproveCard) return;
    setCardLifecycleAction('approve');
    setError('');
    try {
      const updated = await api.approveWorkbenchCard(selectedId, selectedCardKey, 'approved from workbench review');
      setDetail(updated);
      setCardForm(formFromCard(updated));
      setPublishConflict(null);
      await loadList();
      const approvedVersion = (updated.versions || []).find(version => version.status === 'approved');
      showSaveNotice(approvedVersion ? `Card approved as v${approvedVersion.version_number}.` : 'Card approved.', 'card');
    } catch (e) {
      setError(e.message);
    } finally {
      setCardLifecycleAction(null);
    }
  };

  const publishCard = async () => {
    if (!selectedId || !selectedCardKey || !canPublishCard) return;
    setCardLifecycleAction('publish');
    setError('');
    try {
      const updated = await api.publishWorkbenchCard(selectedId, selectedCardKey, 'published from workbench review');
      setDetail(updated);
      setCardForm(formFromCard(updated));
      setPublishConflict(null);
      await loadList();
      showSaveNotice('Card published.', 'card');
      if (queueSession && !queueSession.completed) await advanceQueue();
    } catch (e) {
      const staleConflict = e.detail?.reason === 'approved_version_stale' ? e.detail : null;
      if (staleConflict) {
        setPublishConflict(staleConflict);
        setActiveTab('review');
        setError(staleConflict.message || e.message);
      } else {
        setError(e.message);
      }
    } finally {
      setCardLifecycleAction(null);
    }
  };

  const createTag = async name => {
    const tag = await api.createTag({ name, status: 'pending' });
    setTags(current => [...current.filter(item => item.name !== tag.name), tag]);
    return tag;
  };

  const handlePlaceCreated = async created => {
    setSavedView('all');
    setPage(0);
    await loadList();
    await loadDetail(created.place_master_id, created.card_key || 'primary', 'card');
  };

  const handleCsvImported = async () => {
    setPage(0);
    await loadList();
  };

  const selectCard = cardKey => {
    if (cardKey === NEW_CARD_KEY) {
      setActiveTab('card');
      return;
    }
    loadDetail(selectedId, cardKey, activeTab === 'place' ? 'card' : activeTab);
  };

  const archiveCard = async card => {
    if (!selectedId || mutatingCardKey) return;
    const label = card.card_title || labelize(card.card_key);
    const reason = window.prompt(`Archive '${label}'?\n\nAdd the archive note. This is required and will be stored with the archived version.`);
    if (reason === null) return;
    const archiveNote = reason.trim();
    if (!archiveNote) {
      setError('Archive note is required.');
      return;
    }
    if (!window.confirm(`Archive '${label}'? It will be removed from retrieval and hidden from this list.`)) return;
    setMutatingCardKey(card.card_key);
    setError('');
    try {
      const updated = await api.archiveWorkbenchCard(selectedId, card.card_key, archiveNote);
      if (selectedCardKey === card.card_key) {
        setDetail(updated);
        setCardForm(formFromCard(updated));
      } else {
        setDetail(current => current ? { ...current, cards: updated.cards, card_count: updated.card_count } : current);
      }
      setPlace(current => current ? { ...current, cards: updated.cards, card_count: updated.card_count } : current);
      if (!showArchivedCards && selectedCardKey === card.card_key) {
        const nextCard = (updated.cards || []).find(item => !item.is_archived && !item.archived_at);
        if (nextCard) await loadDetail(selectedId, nextCard.card_key, 'place');
        else setActiveTab('place');
      }
      await loadList();
      showSaveNotice('Card archived.');
    } catch (e) {
      setError(e.message);
    } finally {
      setMutatingCardKey(null);
    }
  };

  const restoreCard = async card => {
    if (!selectedId || mutatingCardKey) return;
    setMutatingCardKey(card.card_key);
    setError('');
    try {
      const updated = await api.restoreWorkbenchCard(selectedId, card.card_key);
      setDetail(updated);
      setCardForm(formFromCard(updated));
      setPlace(current => current ? { ...current, cards: updated.cards, card_count: updated.card_count } : current);
      setSelectedCardKey(updated.card_key || card.card_key);
      setActiveTab('card');
      await loadList();
      showSaveNotice('Card restored.', 'card');
    } catch (e) {
      setError(e.message);
    } finally {
      setMutatingCardKey(null);
    }
  };

  const deleteCard = async card => {
    if (!selectedId || mutatingCardKey) return;
    if (!window.confirm(`Permanently delete '${card.card_title || labelize(card.card_key)}'? This cannot be undone.`)) return;
    setMutatingCardKey(card.card_key);
    setError('');
    try {
      const updatedPlace = await api.deleteWorkbenchCard(selectedId, card.card_key);
      setPlace(updatedPlace);
      setPlaceForm(formFromPlace(updatedPlace));
      const remainingCards = updatedPlace.cards || [];
      const preservedCard = selectedCardKey !== card.card_key
        ? remainingCards.find(item => item.card_key === selectedCardKey)
        : null;
      const nextCard = preservedCard || remainingCards.find(item => !item.is_archived) || remainingCards[0];
      if (nextCard) await loadDetail(selectedId, nextCard.card_key, preservedCard ? activeTab : 'place');
      await loadList();
      showSaveNotice('Card deleted.');
    } catch (e) {
      setError(e.message);
    } finally {
      setMutatingCardKey(null);
    }
  };

  const startAddCard = () => {
    if (creatingCard || !detail) return;
    const draft = newCardDraft(place, detail);
    setDraftReturnCardKey(selectedCardKey);
    setCreatingCard(true);
    setDetail(draft);
    setCardForm(formFromCard(draft));
    setSelectedCardKey(NEW_CARD_KEY);
    setActiveTab('card');
    setActionsOpen(false);
    setError('');
  };

  const cancelAddCard = () => {
    const returnCardKey = draftReturnCardKey || (place?.cards || []).find(card => !card.is_archived)?.card_key;
    setCreatingCard(false);
    setDraftReturnCardKey(null);
    if (returnCardKey) loadDetail(selectedId, returnCardKey, 'card');
  };

  const createCard = async ({ manageSaving = true, showFeedback = true } = {}) => {
    if (!selectedId || saving || !creatingCard) return detail;
    const cardTitle = text(cardForm.card_title);
    if (!cardTitle) {
      setError('Card title is required.');
      return detail;
    }
    const titleConflict = (detail?.cards || place?.cards || []).some(
      card => !card.is_unsaved && normalizedCardTitle(card.card_title) === normalizedCardTitle(cardTitle)
    );
    if (titleConflict) {
      setError(`Card title '${cardTitle}' already exists for this place.`);
      return detail;
    }
    const baseCardKey = cardTitle.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '') || 'card';
    const existingKeys = new Set((detail?.cards || place?.cards || []).filter(card => !card.is_unsaved).map(card => card.card_key));
    let cardKey = baseCardKey;
    let suffix = 2;
    while (existingKeys.has(cardKey)) {
      cardKey = `${baseCardKey}_${suffix}`;
      suffix += 1;
    }
    if (manageSaving) setSaving(true);
    setSavedTarget(null);
    setError('');
    try {
      const created = await api.addWorkbenchCardForPlace(selectedId, {
        ...cardForm,
        card_key: cardKey,
        card_title: cardTitle,
      });
      setCreatingCard(false);
      setDraftReturnCardKey(null);
      await loadDetail(selectedId, created.card_key || cardKey, 'card');
      await loadList();
      if (showFeedback) showSaveNotice('Card created.', 'card');
      return created;
    } catch (e) {
      setError(e.message);
      throw e;
    } finally {
      if (manageSaving) setSaving(false);
    }
  };

  const openQueueItem = async (index, session = queueSession) => {
    const item = session?.items?.[index];
    if (!item) {
      setQueueSession(null);
      return;
    }
    setQueueSession(current => current ? { ...current, index } : current);
    setFocusField(queueFocusField(item));
    await loadDetail(item.place_master_id, item.card_key, queueFocusTab(item, session.viewId));
  };

  const startQueue = async () => {
    const items = filteredCards.filter(card => card.card_key).map(card => ({
      place_master_id: card.place_master_id,
      card_key: card.card_key,
      place_name: card.place_name,
      card_title: card.card_title,
      card_count: card.card_count,
      publish_readiness: card.publish_readiness,
      inbox_count: cardInboxCount(card),
      pending_change_request_count: card.pending_change_request_count,
      pending_signal_proposal_count: card.pending_signal_proposal_count,
    }));
    if (items.length === 0) return;
    const selectedKey = `${selectedId || ''}:${selectedCardKey || ''}`;
    const selectedIndex = items.findIndex(item => cardIdentity(item) === selectedKey);
    const session = {
      viewId: savedView,
      label: activeSavedView.label,
      filters: activeSavedView.filters || {},
      search,
      page,
      items,
      index: selectedIndex >= 0 ? selectedIndex : 0,
      startedAt: new Date().toISOString(),
    };
    setQueueSession(session);
    await openQueueItem(session.index, session);
  };

  const stopQueue = () => {
    setQueueSession(null);
  };

  const advanceQueue = async () => {
    if (!queueSession) return;
    const nextIndex = queueSession.index + 1;
    if (nextIndex >= queueSession.items.length) {
      setQueueSession(current => current ? { ...current, completed: true } : current);
      return;
    }
    await openQueueItem(nextIndex, queueSession);
  };

  const previousQueueItem = async () => {
    if (!queueSession) return;
    const previousIndex = Math.max(0, queueSession.index - 1);
    if (previousIndex === queueSession.index) return;
    await openQueueItem(previousIndex, queueSession);
  };

  const skipQueueItem = async () => {
    await advanceQueue();
  };

  const saveAndNext = async () => {
    if (activeTab === 'place') {
      await savePlace();
    } else if (activeTab === 'card' || activeTab === 'signals' || activeTab === 'review') {
      await saveCard();
    }
    await advanceQueue();
  };

  const availableWorkerCommands = WORKER_COMMANDS.filter(command => capabilities?.worker_commands?.[command.id]);
  const workerCommandGroups = [
    {
      scope: 'place',
      label: 'Place actions',
      helper: 'Checks the place record and may create proposals for multiple cards.',
      commands: availableWorkerCommands.filter(command => command.scope === 'place'),
    },
    {
      scope: 'card',
      label: 'Card actions',
      helper: 'Works only on the selected guest-use card.',
      commands: availableWorkerCommands.filter(command => command.scope === 'card'),
    },
  ].filter(group => group.commands.length > 0);

  const openReadinessIssue = (tab, field) => {
    if (tab === 'inbox') {
      onGoToChanges?.(selectedId, selectedCardKey);
      return;
    }
    setActiveTab(tab);
    setFocusField(null);
    if (field) window.setTimeout(() => setFocusField(field), 0);
  };

  const runWorkerCommand = async command => {
    if (!selectedId || !selectedCardKey) return;
    const scopeCopy = command.scope === 'place'
      ? 'This is a place action. It may create Proposals for the place and any cards affected by the place-level source.'
      : `This is a card action for "${selectedCardKey}". It only targets the selected card.`;
    const confirmed = window.confirm(
      `${command.label} will queue a worker.\n\n${scopeCopy}\n\nIt creates Proposals only; it does not overwrite fields, approve, or publish. Continue?`
    );
    if (!confirmed) return;
    setRunningCommand(command.id);
    setError('');
    try {
      await api.runWorkbenchCardCommand(selectedId, selectedCardKey, command.id, 'queued from workbench actions menu');
      setActionsOpen(false);
      await loadDetail(selectedId, selectedCardKey, activeTab);
      await loadList();
    } catch (e) {
      setError(e.message);
    } finally {
      setRunningCommand(null);
    }
  };

  const acceptUnknownComposerSignal = async dimension => {
    if (!selectedId || !selectedCardKey || !dimension) return;
    setAcceptingUnknownDimension(dimension);
    setError('');
    try {
      const updated = await api.acceptUnknownComposerSignal(
        selectedId,
        selectedCardKey,
        dimension,
        'Admin accepted unknown Composer signal coverage from Composer Signals tab.'
      );
      setDetail(updated);
      setCardForm(formFromCard(updated));
      await loadList();
      showSaveNotice(`${labelize(dimension)} accepted as unknown.`, 'signals');
    } catch (e) {
      setError(e.message);
    } finally {
      setAcceptingUnknownDimension(null);
    }
  };

  const closeDetailPanel = () => {
    setDetailOpen(false);
    setActionsOpen(false);
    setCreatingCard(false);
    setFocusField(null);
  };

  const allPlaceCards = detail?.cards || place?.cards || [];
  const archivedCards = allPlaceCards.filter(card => card.is_archived || card.archived_at);
  const activeCards = allPlaceCards.filter(card => !card.is_archived && !card.archived_at);
  const visiblePlaceCards = showArchivedCards ? allPlaceCards : activeCards;
  const selectedPlaceCard = allPlaceCards.find(card => card.card_key === selectedCardKey) || detail;
  const inboxCount = activeProposalItems.length || (
    detail?.inbox_count ?? (
      (detail?.pending_change_request_count || place?.pending_change_request_count || 0)
      + (detail?.pending_signal_proposal_count || place?.pending_signal_proposal_count || 0)
    )
  );
  const queueActive = Boolean(queueSession && !queueSession.completed);
  const queueProgress = queueActive ? `${queueSession.index + 1} / ${queueSession.items.length}` : '';
  const queueCurrent = queueActive ? queueSession.items[queueSession.index] : null;
  const queueFocus = queueCurrent ? queueFocusTab(queueCurrent, queueSession.viewId) : null;
  const latestEnrichmentRun = activeTab !== 'place' ? detail?.latest_enrichment_run : null;

  useEffect(() => {
    const onKeyDown = event => {
      if (isEditableShortcutTarget(event.target)) return;
      const key = event.key.toLowerCase();
      if (key === 'escape' && detailOpen) {
        event.preventDefault();
        closeDetailPanel();
        return;
      }
      if ((event.metaKey || event.ctrlKey) && key === 's') {
        event.preventDefault();
        if (queueSession && !queueSession.completed) {
          saveAndNext();
        } else if (activeTab === 'place') {
          savePlace();
        } else {
          saveCard();
        }
        return;
      }
      if (!queueSession || queueSession.completed || event.metaKey || event.ctrlKey || event.altKey) return;
      if (key === 'j') {
        event.preventDefault();
        advanceQueue();
      }
      if (key === 'k') {
        event.preventDefault();
        previousQueueItem();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  });

  return (
    <div className="space-y-4 text-stone-900">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Card Workbench</h1>
          <p className="text-sm text-stone-500">List to Place to Card. Published content reads from approved card versions only.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setShowAddPlace(true)}
            className="inline-flex items-center gap-2 rounded-lg bg-stone-900 px-3 py-2 text-sm font-medium text-white hover:bg-stone-800"
          >
            <Plus className="h-4 w-4" /> Add Place
          </button>
          <button
            type="button"
            onClick={() => setShowImportCsv(true)}
            className="inline-flex items-center gap-2 rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm text-stone-700 hover:bg-stone-50"
          >
            <Upload className="h-4 w-4" /> Upload CSV
          </button>
          {queueActive && (
            <span className="rounded-full border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs font-medium text-blue-700">
              Queue: {queueSession.label} · {queueProgress}
            </span>
          )}
          <button
            type="button"
            onClick={queueActive ? stopQueue : startQueue}
            disabled={!queueActive && filteredCards.length === 0}
            className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm disabled:opacity-50 ${
              queueActive
                ? 'border border-stone-300 bg-white text-stone-700 hover:bg-stone-50'
                : 'bg-blue-700 font-medium text-white hover:bg-blue-800'
            }`}
          >
            {queueActive ? <Square className="h-4 w-4" /> : <Play className="h-4 w-4" />}
            {queueActive ? 'Stop queue' : 'Start queue'}
          </button>
          <button
            type="button"
            onClick={() => { loadList({ force: true }); loadSavedViewCounts(); if (detailOpen && selectedId) loadDetail(selectedId, selectedCardKey); }}
            className="inline-flex items-center gap-2 rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm text-stone-700 hover:bg-stone-50"
          >
            <RefreshCw className="h-4 w-4" /> Load results
          </button>
        </div>
      </div>

      {error && <ErrorAlert error={error} onClose={() => setError('')} />}
      {countsError && (
        <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          Counts are temporarily unavailable. The card list can still be reviewed; use Load results to retry counts.
        </div>
      )}

      <div className="relative">
        <section className="overflow-hidden rounded-xl border border-stone-200 bg-white">
          <div className="border-b border-stone-200 bg-stone-50 px-4 py-3">
            <div className="flex flex-wrap items-center gap-2">
              {savedViews.map(view => (
                <button
                  type="button"
                  key={view.id}
                  onClick={() => { setSavedView(view.id); setPage(0); setQueueSession(null); }}
                  className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${savedViewChipClass(view.id, savedView === view.id)}`}
                >
                  {view.label}
                  {` · ${savedViewCountLabel(view)}`}
                </button>
              ))}
            </div>
            <div className="mt-3 flex items-center gap-2 rounded-lg border border-stone-300 bg-white px-3 py-2">
              <Search className="h-4 w-4 text-stone-400" />
              <input
                value={searchInput}
                onChange={event => setSearchInput(event.target.value)}
                placeholder="Search place, area, ID, tag"
                className="min-w-0 flex-1 text-sm outline-none"
              />
            </div>
          </div>
          <div className="grid grid-cols-[2fr_0.85fr_1.35fr_0.7fr_0.6fr] gap-3 border-b border-stone-200 px-4 py-2 text-[11px] font-medium uppercase tracking-wide text-stone-400">
            <span>Place</span><span>Stage</span><span>Reason / next step</span><span>Verified</span><span>Proposals</span>
          </div>
          <div className="max-h-[calc(100vh-270px)] overflow-y-auto">
            {loadingList && <p className="p-4 text-sm text-stone-500">Loading cards...</p>}
            {!loadingList && !listLoaded && (
              <p className="p-4 text-sm text-stone-500">Search for a place or choose a saved view to load matching cards. The full card list is no longer loaded automatically.</p>
            )}
            {!loadingList && listLoaded && filteredCards.length === 0 && <p className="p-4 text-sm text-stone-500">No cards match this view.</p>}
            {!loadingList && filteredCards.map(card => (
              <ListRow
                key={`${card.place_master_id}:${card.card_key}`}
                card={card}
                active={detailOpen && selectedId === card.place_master_id && selectedCardKey === card.card_key}
                onOpen={() => loadDetail(card.place_master_id, card.card_key, 'place')}
              />
            ))}
          </div>
          <div className="flex items-center justify-between border-t border-stone-200 px-4 py-3 text-sm text-stone-500">
            <span>{displayedTotal} records</span>
            <div className="flex gap-2">
              <button type="button" disabled={!listLoaded || page === 0} onClick={() => setPage(p => Math.max(0, p - 1))} className="rounded-md border border-stone-300 px-2 py-1 disabled:opacity-40">Prev</button>
              <button type="button" disabled={!listLoaded || (page + 1) * PAGE_SIZE >= total} onClick={() => setPage(p => p + 1)} className="rounded-md border border-stone-300 px-2 py-1 disabled:opacity-40">Next</button>
            </div>
          </div>
        </section>

        <section
          aria-hidden={!detailOpen}
          className={`fixed inset-y-0 right-0 z-40 flex max-w-[calc(100vw-72px)] flex-col overflow-hidden border-l border-stone-200 bg-stone-50 shadow-2xl transition-transform duration-200 ${
            detailOpen ? 'translate-x-0' : 'pointer-events-none translate-x-full'
          }`}
          style={{ width: detailPanelWidth }}
        >
          <div
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize place workbench"
            onMouseDown={event => {
              event.preventDefault();
              setResizingDetailPanel(true);
            }}
            className="absolute inset-y-0 left-0 z-30 flex w-3 cursor-col-resize items-center justify-center border-l border-transparent hover:border-blue-300"
          >
            <span className="rounded-full border border-stone-200 bg-white p-1 text-stone-400 shadow-sm">
              <GripVertical className="h-4 w-4" />
            </span>
          </div>
          {!detail && (
            <div className="relative flex min-h-screen items-center justify-center px-6 text-sm text-stone-500">
              <button
                type="button"
                onClick={closeDetailPanel}
                className="absolute right-4 top-4 inline-flex items-center gap-2 rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm text-stone-700 hover:bg-stone-50"
              >
                <X className="h-4 w-4" /> Close
              </button>
              {loadingDetail ? 'Loading card...' : 'Select a card to open the workbench.'}
            </div>
          )}
          {detail && (
            <>
              <div className="border-b border-stone-200 bg-white px-5 py-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex min-w-0 items-center gap-3">
                    {returnToChanges && (
                      <button
                        type="button"
                        onClick={onReturnToChanges}
                        aria-label="Back to Change Proposals"
                        title="Back to Change Proposals"
                        className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-stone-300 bg-white text-stone-700 hover:bg-stone-50"
                      >
                        <ArrowLeft className="h-4 w-4" />
                      </button>
                    )}
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-stone-200 bg-stone-50 text-stone-600">
                      <Building2 className="h-4 w-4" />
                    </span>
                    <div className="min-w-0">
                      <p className="text-xs font-medium uppercase text-stone-400">Place</p>
                      <h2 className="truncate text-base font-semibold text-stone-900">{placeForm.name || detail.place_name}</h2>
                      <p className="truncate text-xs text-stone-500">{placeForm.area || 'Unknown area'} · {selectedId}</p>
                    </div>
                  </div>
                  <button type="button" onClick={closeDetailPanel} aria-label="Close workbench" title="Close workbench" className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-stone-300 bg-white text-stone-600 hover:bg-stone-50">
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>

              <div className="flex min-h-0 flex-1 overflow-hidden">
                <aside className="hidden w-56 shrink-0 flex-col border-r border-stone-200 bg-white md:flex">
                  <div className="p-3">
                    <p className="mb-2 px-2 text-[11px] font-medium uppercase text-stone-400">Shared record</p>
                    <button
                      type="button"
                      onClick={() => { setActiveTab('place'); setActionsOpen(false); }}
                      className={`flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-left text-sm ${
                        activeTab === 'place' ? 'bg-stone-900 font-medium text-white' : 'text-stone-700 hover:bg-stone-100'
                      }`}
                    >
                      <Building2 className="h-4 w-4 shrink-0" />
                      <span className="min-w-0 flex-1 truncate">Place details</span>
                    </button>
                  </div>
                  <div className="flex min-h-0 flex-1 flex-col border-t border-stone-200 p-3">
                    <div className="mb-2 flex items-center justify-between gap-2 px-2">
                      <p className="text-[11px] font-medium uppercase text-stone-400">Cards · {activeCards.length}</p>
                      <button
                        type="button"
                        onClick={creatingCard ? cancelAddCard : startAddCard}
                        aria-label={creatingCard ? 'Cancel new card' : 'Create card'}
                        title={creatingCard ? 'Cancel new card' : 'Create card'}
                        className="inline-flex h-7 w-7 items-center justify-center rounded-md text-stone-500 hover:bg-stone-100 hover:text-stone-900"
                      >
                        {creatingCard ? <X className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                      </button>
                    </div>
                    {archivedCards.length > 0 && (
                      <label className="mb-2 flex cursor-pointer items-center gap-2 px-2 text-xs text-stone-600">
                        <input
                          type="checkbox"
                          checked={showArchivedCards}
                          onChange={event => setShowArchivedCards(event.target.checked)}
                          className="h-4 w-4 rounded border-stone-300 text-stone-900 focus:ring-stone-500"
                        />
                        <span>Show archived ({archivedCards.length})</span>
                      </label>
                    )}
                    <div className="min-h-0 space-y-1 overflow-y-auto">
                      {visiblePlaceCards.map(card => {
                        const cardActive = activeTab !== 'place' && card.card_key === selectedCardKey;
                        const isArchived = Boolean(card.is_archived || card.archived_at);
                        const isUnsaved = Boolean(card.is_unsaved);
                        const displayTitle = isUnsaved ? (text(cardForm.card_title) || 'New card') : (card.card_title || labelize(card.card_key));
                        const lifecycleLabel = card.card_key === selectedCardKey && cardHasUnsavedChanges
                          ? 'Unsaved changes'
                          : cardLifecycleLabel(card.card_key === selectedCardKey ? detail : card);
                        const lifecycleDot = lifecycleLabel === 'Published'
                          ? 'bg-green-500'
                          : lifecycleLabel === 'Approved'
                            ? 'bg-blue-500'
                            : lifecycleLabel.includes('blocked')
                              ? 'bg-amber-500'
                              : 'bg-stone-400';
                        return (
                          <div
                            key={card.card_key}
                            className={`group flex items-center rounded-lg border ${
                              cardActive ? 'border-blue-200 bg-blue-50' : 'border-transparent hover:bg-stone-100'
                            } ${isArchived ? 'text-stone-400' : 'text-stone-700'}`}
                          >
                            <button
                              type="button"
                              onClick={() => selectCard(card.card_key)}
                              className="min-w-0 flex-1 px-3 py-2.5 text-left"
                            >
                              <span className="flex items-start gap-2">
                                <CreditCard className={`mt-0.5 h-4 w-4 shrink-0 ${cardActive ? 'text-blue-700' : 'text-stone-400'}`} />
                                <span className="min-w-0 flex-1">
                                  <span className={`block truncate text-sm ${cardActive ? 'font-medium text-blue-900' : ''}`}>{displayTitle}</span>
                                  <span className="mt-0.5 flex items-center gap-1.5 text-[11px] text-stone-500">
                                    <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${lifecycleDot}`} />
                                    <span className="truncate">{lifecycleLabel}</span>
                                  </span>
                                </span>
                              </span>
                            </button>
                            {isUnsaved && (
                              <div className="flex shrink-0 items-center pr-1">
                                <button type="button" onClick={cancelAddCard} aria-label="Cancel new card" title="Cancel new card" className="inline-flex h-8 w-8 items-center justify-center rounded-md text-stone-400 hover:bg-white hover:text-stone-800">
                                  <X className="h-4 w-4" />
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </aside>

                <div className="flex min-w-0 flex-1 flex-col">
                  <div className="border-b border-stone-200 bg-white p-3 md:hidden">
                    <div className="flex gap-2 overflow-x-auto">
                      <button type="button" onClick={() => setActiveTab('place')} className={`shrink-0 rounded-lg border px-3 py-2 text-sm ${activeTab === 'place' ? 'border-stone-900 bg-stone-900 text-white' : 'border-stone-300 text-stone-700'}`}>
                        Place
                      </button>
                      {visiblePlaceCards.map(card => (
                        <button type="button" key={card.card_key} onClick={() => selectCard(card.card_key)} className={`shrink-0 rounded-lg border px-3 py-2 text-sm ${activeTab !== 'place' && selectedCardKey === card.card_key ? 'border-blue-200 bg-blue-50 font-medium text-blue-900' : 'border-stone-300 text-stone-700'}`}>
                          {card.is_unsaved ? (text(cardForm.card_title) || 'New card') : (card.card_title || labelize(card.card_key))} · {card.card_key === selectedCardKey && cardHasUnsavedChanges ? 'Unsaved changes' : cardLifecycleLabel(card.card_key === selectedCardKey ? detail : card)}
                        </button>
                      ))}
                      <button type="button" onClick={creatingCard ? cancelAddCard : startAddCard} aria-label={creatingCard ? 'Cancel new card' : 'Create card'} title={creatingCard ? 'Cancel new card' : 'Create card'} className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-stone-300 text-stone-600">
                        {creatingCard ? <X className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                      </button>
                    </div>
                    {archivedCards.length > 0 && (
                      <label className="mt-2 flex cursor-pointer items-center gap-2 text-xs text-stone-600">
                        <input
                          type="checkbox"
                          checked={showArchivedCards}
                          onChange={event => setShowArchivedCards(event.target.checked)}
                          className="h-4 w-4 rounded border-stone-300 text-stone-900 focus:ring-stone-500"
                        />
                        <span>Show archived ({archivedCards.length})</span>
                      </label>
                    )}
                  </div>

                  {latestEnrichmentRun && (
                    <div className="border-b border-stone-200 bg-white px-5 py-3">
                      <div className="flex flex-wrap items-center gap-2 text-xs text-stone-600">
                        <Badge kind={latestEnrichmentRunKind(latestEnrichmentRun)}>Latest enrichment</Badge>
                        <span className="font-medium text-stone-800">{latestEnrichmentRunMessage(latestEnrichmentRun)}</span>
                        {latestEnrichmentRun.model_name && <span>{labelize(latestEnrichmentRun.model_name)}</span>}
                        {(latestEnrichmentRun.completed_at || latestEnrichmentRun.created_at) && (
                          <span>{formatDate(latestEnrichmentRun.completed_at || latestEnrichmentRun.created_at)}</span>
                        )}
                        {latestEnrichmentRun.status === 'completed' && !latestEnrichmentRun.has_actionable_items && (
                          <span className="text-stone-500">No Change Requests were created because the run found no new actionable deltas.</span>
                        )}
                      </div>
                    </div>
                  )}

                  {activeTab !== 'place' && detail.is_archived && (
                    <div className="border-b border-amber-200 bg-amber-50 px-5 py-3">
                      <p className="text-sm text-amber-900">This card is archived and read-only.</p>
                      {detail.archive_note && <p className="mt-1 text-sm text-amber-900">Archive note: {detail.archive_note}</p>}
                    </div>
                  )}

                  {activeTab !== 'place' && publishedCardLocked && (
                    <div className="border-b border-blue-200 bg-blue-50 px-5 py-3">
                      <p className="text-sm text-blue-900">This Card is published and read-only. Archive it to remove it from live retrieval; restoring it creates an editable draft.</p>
                    </div>
                  )}

                  <div className="border-b border-stone-200 bg-white px-5 py-3">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex min-w-0 items-center gap-2">
                          {activeTab === 'place' ? <Building2 className="h-4 w-4 shrink-0 text-stone-400" /> : <CreditCard className="h-4 w-4 shrink-0 text-stone-400" />}
                          <h3 className="truncate text-sm font-semibold text-stone-900">{activeTab === 'place' ? 'Place details' : (detail.is_unsaved ? (text(cardForm.card_title) || 'New card') : (detail.card_title || labelize(selectedCardKey)))}</h3>
                        </div>
                        <p className="mt-1 truncate pl-6 text-xs text-stone-500">{activeTab === 'place' ? 'Identity, location, contacts, and sources' : (placeForm.name || detail.place_name)}</p>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        {queueActive && (
                          <>
                            <button type="button" onClick={saveAndNext} disabled={saving || loadingDetail || detail.is_archived || creatingCard || (activeTab !== 'place' && publishedCardLocked)} className="inline-flex min-w-[128px] items-center justify-center gap-2 rounded-lg bg-blue-700 px-3 py-2 text-sm font-medium text-white hover:bg-blue-800 disabled:opacity-50">
                              {saving ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                              {saving ? 'Saving...' : 'Save & next'}
                            </button>
                            <button type="button" onClick={previousQueueItem} disabled={saving || loadingDetail || queueSession.index === 0} aria-label="Previous card" title="Previous card" className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-stone-300 text-stone-700 hover:bg-stone-50 disabled:opacity-50">
                              <ArrowLeft className="h-4 w-4" />
                            </button>
                            <button type="button" onClick={skipQueueItem} disabled={saving || loadingDetail} aria-label="Skip card" title="Skip card" className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-stone-300 text-stone-700 hover:bg-stone-50 disabled:opacity-50">
                              <SkipForward className="h-4 w-4" />
                            </button>
                          </>
                        )}
                        {activeTab === 'place' ? !queueActive && (
                          <button type="button" onClick={savePlace} disabled={saving} className="inline-flex min-w-[122px] items-center justify-center gap-2 rounded-lg bg-stone-900 px-3 py-2 text-sm font-medium text-white hover:bg-stone-800 disabled:opacity-50">
                            {saving ? <RefreshCw className="h-4 w-4 animate-spin" /> : savedTarget === 'place' ? <CheckCircle2 className="h-4 w-4" /> : <Save className="h-4 w-4" />}
                            {saving ? 'Saving...' : savedTarget === 'place' ? 'Saved' : 'Save place'}
                          </button>
                        ) : (
                          <>
                            {creatingCard && (
                              <button type="button" onClick={cancelAddCard} disabled={saving} aria-label="Cancel new card" title="Cancel new card" className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-stone-300 bg-white text-stone-600 hover:bg-stone-50 disabled:opacity-50">
                                <X className="h-4 w-4" />
                              </button>
                            )}
                            {(!queueActive || creatingCard) && !detail.is_archived && !publishedCardLocked && (
                              <button
                                type="button"
                                onClick={() => saveCard()}
                                disabled={saving || cardLifecycleAction !== null || (!creatingCard && !cardHasUnsavedChanges)}
                                title={cardHasUnsavedChanges ? 'Save the working copy' : 'The working copy is saved'}
                                className="inline-flex min-w-[120px] items-center justify-center gap-2 rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm text-stone-700 hover:bg-stone-50 disabled:opacity-60"
                              >
                                {saving ? <RefreshCw className="h-4 w-4 animate-spin" /> : !cardHasUnsavedChanges ? <CheckCircle2 className="h-4 w-4 text-green-700" /> : <Save className="h-4 w-4" />}
                                {saving ? (creatingCard ? 'Creating...' : 'Saving...') : !cardHasUnsavedChanges ? 'Saved' : 'Save card'}
                              </button>
                            )}
                            {detail.is_archived && (
                              <>
                                <button type="button" disabled={Boolean(mutatingCardKey) || cardLifecycleAction !== null} onClick={() => restoreCard(selectedPlaceCard)} className="inline-flex items-center gap-2 rounded-lg border border-amber-300 bg-white px-3 py-2 text-sm font-medium text-amber-900 hover:bg-amber-50 disabled:opacity-50">
                                  {mutatingCardKey === selectedCardKey ? <RefreshCw className="h-4 w-4 animate-spin" /> : <ArchiveRestore className="h-4 w-4" />} Restore
                                </button>
                                <button type="button" disabled={Boolean(mutatingCardKey) || cardLifecycleAction !== null} onClick={() => deleteCard(selectedPlaceCard)} aria-label="Delete card permanently" title="Delete card permanently" className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-red-200 bg-white text-red-700 hover:bg-red-50 disabled:opacity-50">
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </>
                            )}
                            {!creatingCard && !detail.is_archived && (
                              <button type="button" onClick={() => archiveCard(selectedPlaceCard)} disabled={Boolean(mutatingCardKey) || saving || cardLifecycleAction !== null} aria-label="Archive card" title="Archive card" className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-stone-300 bg-white text-stone-600 hover:bg-stone-50 disabled:opacity-50">
                                {mutatingCardKey === selectedCardKey ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Archive className="h-4 w-4" />}
                              </button>
                            )}
                            {availableWorkerCommands.length > 0 && !detail.is_archived && !creatingCard && (
                              <div className="relative">
                                <button
                                  type="button"
                                  onClick={() => setActionsOpen(open => !open)}
                                  disabled={runningCommand !== null}
                                  className="inline-flex min-w-[104px] items-center justify-center gap-2 rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm font-medium text-stone-700 hover:bg-stone-50 disabled:opacity-50"
                                >
                                  <Play className="h-4 w-4" />
                                  Actions
                                </button>
                                {actionsOpen && (
                                  <div className="absolute right-0 z-20 mt-2 w-72 rounded-lg border border-stone-200 bg-white p-2 shadow-lg">
                                    {workerCommandGroups.map(group => (
                                      <div key={group.scope} className="py-1 first:pt-0 last:pb-0">
                                        <div className="px-3 pb-1 pt-2 first:pt-1">
                                          <p className="text-xs font-semibold uppercase tracking-wide text-stone-500">{group.label}</p>
                                          <p className="mt-0.5 text-xs leading-4 text-stone-400">{group.helper}</p>
                                        </div>
                                        {group.commands.map(command => (
                                          <button key={command.id} type="button" onClick={() => runWorkerCommand(command)} disabled={runningCommand !== null} className="block w-full rounded-lg px-3 py-2 text-left text-sm text-stone-700 hover:bg-stone-50 disabled:opacity-50">
                                            {runningCommand === command.id ? 'Queueing...' : command.label}
                                          </button>
                                        ))}
                                      </div>
                                    ))}
                                    <div className="mt-2 border-t border-stone-200 px-3 py-2 text-xs leading-5 text-stone-500">
                                      Results arrive as proposals. Nothing is overwritten, approved, or published.
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  {activeTab !== 'place' && (
                    <div className={`border-b px-5 py-4 ${stageBannerClass(stageBadgeKind(detail))}`}>
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <Badge kind={stageBadgeKind(detail)}>{stageLabel(detail)}</Badge>
                            {detail.live_version && <Badge kind="success">Live v{detail.live_version}</Badge>}
                            {cardHasUnsavedChanges && <Badge kind="warning">Unsaved changes</Badge>}
                            {correctionProposalCount > 0 && detail.stage === 'ready_to_approve' && (
                              <Badge kind="accent">{correctionProposalCount} pending correction{correctionProposalCount === 1 ? '' : 's'}</Badge>
                            )}
                          </div>
                          <p className="mt-2 text-sm font-medium">{stageBannerSentence(detail)}</p>
                          {detail.stage === 'ready_to_approve' && correctionProposalCount > 0 && (
                            <button
                              type="button"
                              onClick={() => onGoToChanges?.(selectedId, selectedCardKey)}
                              className="mt-2 text-xs font-medium text-blue-800 underline underline-offset-2"
                            >
                              Review pending corrections before approving
                            </button>
                          )}
                        </div>
                        <div className="flex shrink-0 flex-wrap items-center gap-2">
                          {detail.stage === 'needs_work' && (
                            <button type="button" onClick={() => setActiveTab('review')} className="inline-flex items-center gap-2 rounded-lg border border-yellow-300 bg-white px-3 py-2 text-sm font-medium text-yellow-900 hover:bg-yellow-50">
                              <AlertTriangle className="h-4 w-4" /> View issues
                            </button>
                          )}
                          {detail.stage === 'attention' && (
                            <button type="button" onClick={() => (blockingProposalCount > 0 || inboxCount > 0 ? onGoToChanges?.(selectedId, selectedCardKey) : setActiveTab('review'))} className="inline-flex items-center gap-2 rounded-lg border border-red-200 bg-white px-3 py-2 text-sm font-medium text-red-800 hover:bg-red-50">
                              <AlertTriangle className="h-4 w-4" /> {blockingProposalCount > 0 || inboxCount > 0 ? 'View proposals' : 'View issues'}
                            </button>
                          )}
                          {detail.stage === 'ready_to_approve' && (
                            <button
                              type="button"
                              data-focus="approve-publish"
                              onClick={approveCard}
                              disabled={!canApproveCard || saving || loadingDetail || cardLifecycleAction !== null}
                              title={cardHasUnsavedChanges ? 'Save this card before approving it' : !readyForApproval ? 'Resolve readiness issues before approving' : 'Approve the current saved working copy'}
                              className={`inline-flex min-w-[112px] items-center justify-center gap-2 rounded-lg bg-blue-700 px-3 py-2 text-sm font-medium text-white hover:bg-blue-800 disabled:opacity-50 ${focusField === 'approve-publish' ? 'ring-2 ring-blue-300' : ''}`}
                            >
                              {cardLifecycleAction === 'approve' ? <RefreshCw className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                              {cardLifecycleAction === 'approve' ? 'Approving...' : 'Approve'}
                            </button>
                          )}
                          {detail.stage === 'approved' && (
                            <button
                              type="button"
                              onClick={publishCard}
                              disabled={!canPublishCard || saving || loadingDetail || cardLifecycleAction !== null}
                              title={!currentState.publishable ? 'Resolve readiness issues before publishing' : 'Publish the current approved version'}
                              className="inline-flex min-w-[112px] items-center justify-center gap-2 rounded-lg bg-stone-900 px-3 py-2 text-sm font-medium text-white hover:bg-stone-800 disabled:opacity-50"
                            >
                              {cardLifecycleAction === 'publish' ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                              {cardLifecycleAction === 'publish' ? 'Publishing...' : 'Publish'}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {activeTab !== 'place' && <WorkbenchTabs active={activeTab} onChange={setActiveTab} reviewCount={reviewState.issueCount} versionCount={(detail.versions || []).length} signalIssueCount={composerSignalIssueCount} />}

                  <div className="flex-1 overflow-y-auto p-5">
                {queueActive && (
                  <div className="mb-4 rounded-xl border border-blue-200 bg-blue-50/50 p-3 text-sm text-blue-900">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge kind="accent">Queue mode</Badge>
                      <span>{queueSession.label} · {queueProgress}</span>
                      {queueFocus && <span className="text-blue-700">Focused tab: {labelize(queueFocus)}</span>}
                      {queueSession.search && <span className="text-blue-700">Search: {queueSession.search}</span>}
                    </div>
                  </div>
                )}
                {queueSession?.completed && (
                  <div className="mb-4 rounded-xl border border-green-200 bg-green-50 p-3 text-sm text-green-800">
                    Queue complete for {queueSession.label}. Start another queue from the active view when ready.
                  </div>
                )}
                {loadingDetail && <p className="mb-4 text-sm text-stone-500">Loading detail...</p>}

                {activeTab === 'card' && (
                  <fieldset disabled={publishedCardLocked} className={`grid gap-4 ${publishedCardLocked ? 'opacity-75' : ''}`}>
                    <Section eyebrow="Guest-facing" title="Guest Card Core">
                      <div className="grid gap-3">
                        <TextInput label="Card title" value={cardForm.card_title} onChange={value => setCardForm(f => ({ ...f, card_title: value }))} />
                        <TextArea label="Pitch" rows={4} value={cardForm.pitch} onChange={value => setCardForm(f => ({ ...f, pitch: value }))} placeholder="Why this card is worth recommending." warning={!text(cardForm.pitch) && !text(detail.good_for)} focusKey="pitch" highlight={focusField === 'pitch'} />
                        <TextArea label="Knows" rows={3} value={cardForm.caveat} onChange={value => setCardForm(f => ({ ...f, caveat: value }))} placeholder="Fit and practical guidance for guests." warning={!text(cardForm.caveat)} focusKey="caveat" highlight={focusField === 'caveat'} />
                      </div>
                    </Section>
                    <DatedEventsSection events={detail.dated_events || []} />
                    <Section eyebrow="Composer-only · never shown to guests" title="Agent Guidance">
                      <TextArea label="Card note for agent" rows={3} value={cardForm.card_note_for_agent} onChange={value => setCardForm(f => ({ ...f, card_note_for_agent: value }))} placeholder="How Composer should use this card. Not verbatim guest copy." />
                    </Section>
                    <Section eyebrow="Planning signals" title="Taxonomy and Attributes">
                      <div className="grid gap-4">
                        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                          <SelectField label="Retrieval lane" value={cardForm.retrieval_lane} options={RETRIEVAL_LANE_OPTIONS} onChange={value => setCardForm(f => ({ ...f, retrieval_lane: value }))} required />
                          <SelectField label="Card Category" value={cardForm.category} options={vocab.category || []} onChange={value => setCardForm(f => ({ ...f, category: value }))} required focusKey="card-category" highlight={focusField === 'card-category'} />
                          <SelectField label="Budget" value={cardForm.budget} options={vocab.budget || []} onChange={value => setCardForm(f => ({ ...f, budget: value }))} />
                          <SelectField label="Seasonality" value={cardForm.seasonality} options={vocab.seasonality || []} onChange={value => setCardForm(f => ({ ...f, seasonality: value }))} />
                        </div>
                        <CardTags tags={tags} selected={cardForm.guest_use_case_tags} onChange={value => setCardForm(f => ({ ...f, guest_use_case_tags: value }))} onCreate={createTag} />
                        <div className="grid gap-4 md:grid-cols-2">
                          <MultiPicker label="Booking logic" selected={cardForm.booking_logic} options={vocab.booking_logic || []} onChange={value => setCardForm(f => ({ ...f, booking_logic: value }))} />
                          <MultiPicker label="Weather logic" selected={cardForm.weather_logic} options={vocab.weather_logic || []} onChange={value => setCardForm(f => ({ ...f, weather_logic: value }))} />
                          <MultiPicker label="Arrival & parking" selected={cardForm.parking_access} options={vocab.parking_access || []} onChange={value => setCardForm(f => ({ ...f, parking_access: value }))} accent />
                          <SelectField label="Freshness risk" value={cardForm.freshness_risk} options={FRESHNESS_RISK_OPTIONS} onChange={value => setCardForm(f => ({ ...f, freshness_risk: value }))} focusKey="freshness-risk" highlight={focusField === 'freshness-risk'} />
                        </div>
                        {cardForm.retrieval_lane === 'practical_find' && (
                          <div className="grid gap-3 md:grid-cols-[220px_1fr]">
                            <SelectField label="Utility kind" value={cardForm.utility_kind} options={vocab.utility_kind || []} onChange={value => setCardForm(f => ({ ...f, utility_kind: value }))} focusKey="utility-kind" highlight={focusField === 'utility-kind'} />
                            <TextArea label="Practical note" rows={2} value={cardForm.practical_note} onChange={value => setCardForm(f => ({ ...f, practical_note: value }))} focusKey="practical-note" highlight={focusField === 'practical-note'} />
                          </div>
                        )}
                      </div>
                    </Section>
                    {(detail.short_desc_guest || detail.good_for || detail.suits) && (
                      <details className="rounded-xl border border-stone-200 bg-white p-4 text-sm text-stone-500">
                        <summary className="cursor-pointer font-medium text-stone-700">Legacy fields</summary>
                        <div className="mt-3 grid gap-2">
                          <p><span className="font-medium">Short copy:</span> {detail.short_desc_guest || '-'}</p>
                          <p><span className="font-medium">Good for:</span> {detail.good_for || '-'}</p>
                          <p><span className="font-medium">Suits:</span> {detail.suits || '-'}</p>
                        </div>
                      </details>
                    )}
                  </fieldset>
                )}

                {activeTab === 'signals' && (
                  <ComposerSignalsTab
                    cardForm={cardForm}
                    detail={detail}
                    approvedMatchSignals={approvedMatchSignals}
                    pendingMatchSignals={pendingMatchSignals}
                    acceptedUnknownMatchSignals={acceptedUnknownMatchSignals}
                    onEditContent={() => setActiveTab('card')}
                    onReviewChanges={() => onGoToChanges?.(selectedId, selectedCardKey)}
                    onEnrichCard={() => enrichCardCommand && runWorkerCommand(enrichCardCommand)}
                    onAcceptUnknown={acceptUnknownComposerSignal}
                    acceptingUnknownDimension={acceptingUnknownDimension}
                    canRunEnrichment={Boolean(enrichCardCommand) && runningCommand === null && !detail?.is_archived && !publishedCardLocked}
                  />
                )}

                {activeTab === 'place' && (
                  <div className="grid gap-4">
                    <div data-focus="place-identity" className={focusField === 'place-identity' ? 'rounded-xl ring-2 ring-blue-300' : ''}>
                      <Section eyebrow="Place identity" title={placeForm.name || detail.place_name}>
                        <div className="grid gap-3 md:grid-cols-2">
                          <TextInput label="Place name" value={placeForm.name} onChange={value => setPlaceForm(f => ({ ...f, name: value }))} focusKey="place-name" highlight={focusField === 'place-name'} />
                          <TextInput label="Area" value={placeForm.area} onChange={value => setPlaceForm(f => ({ ...f, area: value }))} focusKey="place-area" highlight={focusField === 'place-area'} />
                          <TextInput label="Town / community" value={placeForm.town_or_community} onChange={value => setPlaceForm(f => ({ ...f, town_or_community: value }))} />
                          <TextInput label="Address" value={placeForm.address} onChange={value => setPlaceForm(f => ({ ...f, address: value }))} />
                          <SelectField label="Place Sector" value={placeForm.sector_primary} options={vocab.sector || []} onChange={value => setPlaceForm(f => ({ ...f, sector_primary: value }))} />
                          <SelectField label="Place Category" value={placeForm.category_type} options={vocab.category_type || []} onChange={value => setPlaceForm(f => ({ ...f, category_type: value }))} />
                          <div className="md:col-span-2">
                            <TextArea
                              label="Place-level internal notes"
                              rows={3}
                              value={placeForm.operator_review_note}
                              onChange={value => setPlaceForm(f => ({ ...f, operator_review_note: value }))}
                              placeholder="Admin-only context about the place or source status. Not guest-facing."
                            />
                          </div>
                          <div data-focus="place-eligibility" className={`md:col-span-2 ${focusField === 'place-eligibility' ? 'rounded-lg ring-2 ring-blue-300' : ''}`}>
                            <p className="mb-2 text-xs font-medium text-stone-500">Recommendation eligibility</p>
                            <div className="flex flex-wrap gap-x-6 gap-y-2 rounded-lg border border-stone-300 bg-stone-50 px-3 py-2.5">
                              <label className="flex items-center gap-2 text-sm text-stone-700">
                                <input
                                  type="checkbox"
                                  checked={placeForm.eligible_for_pocket_plan}
                                  onChange={event => setPlaceForm(form => ({ ...form, eligible_for_pocket_plan: event.target.checked }))}
                                  className="h-4 w-4 rounded border-stone-300 text-stone-900 focus:ring-stone-500"
                                />
                                Pocket Plan
                              </label>
                              <label className="flex items-center gap-2 text-sm text-stone-700">
                                <input
                                  type="checkbox"
                                  checked={placeForm.eligible_for_companion}
                                  onChange={event => setPlaceForm(form => ({ ...form, eligible_for_companion: event.target.checked }))}
                                  className="h-4 w-4 rounded border-stone-300 text-stone-900 focus:ring-stone-500"
                                />
                                Companion
                              </label>
                            </div>
                          </div>
                        </div>
                      </Section>
                    </div>
                    <div>
                      <PlaceContactSourceEditor
                        contacts={place?.contacts || []}
                        sources={place?.sources || []}
                        savingKey={savingPlaceRecord}
                        sourceFocusKey="place-source"
                        sourceHighlight={focusField === 'place-source'}
                        onCreateContact={createPlaceContact}
                        onUpdateContact={updatePlaceContact}
                        onDeleteContact={deletePlaceContact}
                        onCreateSource={createPlaceSource}
                        onUpdateSource={updatePlaceSource}
                        onDeleteSource={deletePlaceSource}
                        onCreateManualSourceSnapshot={createManualSourceSnapshot}
                      />
                    </div>
                  </div>
                )}

                {activeTab === 'review' && (
                  <div className="grid gap-4">
                    <Section>
                      <div className={`rounded-lg border p-4 ${currentState.publishable ? 'border-green-200 bg-green-50' : readyForApproval ? 'border-blue-200 bg-blue-50' : 'border-yellow-300 bg-yellow-50'}`}>
                        <div className="flex flex-wrap items-center gap-3">
                          {currentState.publishable || readyForApproval ? <CheckCircle2 className={`h-5 w-5 ${currentState.publishable ? 'text-green-700' : 'text-blue-700'}`} /> : <AlertTriangle className="h-5 w-5 text-yellow-700" />}
                          <p className="text-base font-medium">{currentState.publishable ? 'Publishable' : readyForApproval ? 'Ready for approval' : `Blocked - ${reviewState.issueCount} issue${reviewState.issueCount === 1 ? '' : 's'}`}</p>
                          <span className="ml-auto text-xs text-stone-500">Computed from readiness rules</span>
                        </div>
                        <div className="mt-3 grid gap-2">
                          {reviewState.missing.map(item => (
                            <ReadinessIssueRow
                              key={`missing-${item}`}
                              issue={item}
                              type="missing"
                              cardCount={activeCards.length}
                              taxonomyReview={reviewState.taxonomyReview}
                              onOpen={openReadinessIssue}
                            />
                          ))}
                          {reviewState.blockers.map(item => (
                            <ReadinessIssueRow
                              key={`blocker-${item}`}
                              issue={item}
                              type="blocker"
                              cardCount={activeCards.length}
                              taxonomyReview={reviewState.taxonomyReview}
                              onOpen={openReadinessIssue}
                            />
                          ))}
                          {reviewState.issueCount === 0 && <p className={`rounded-lg border bg-white px-3 py-2 text-sm ${currentState.publishable ? 'border-green-200 text-green-700' : 'border-blue-200 text-blue-700'}`}>{currentState.publishable ? 'No independent readiness issues.' : 'No independent readiness issues. Use Approve to create the immutable Card version.'}</p>}
                        </div>
                      </div>
                    </Section>
                    {publishConflict && (
                      <Section eyebrow="Publish conflict" title={`Approved version v${publishConflict.approved_version_number || '-'}`}>
                        <p className="mb-3 text-sm text-yellow-800">{publishConflict.message || 'Working card differs from the approved version.'}</p>
                        <DiffRows changes={publishConflict.changed_fields || []} previousLabel="Approved" />
                      </Section>
                    )}
                    <Section eyebrow="Published projection" title="Working vs live">
                      {!detail.published_item && <p className="text-sm text-stone-500">No live published projection for this card.</p>}
                      {detail.published_item && publishedDiffs.length === 0 && (
                        <p className="rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700">Working fields match the live projection fields shown here.</p>
                      )}
                      {detail.published_item && publishedDiffs.length > 0 && (
                        <DiffRows changes={publishedDiffs} previousLabel="Live" />
                      )}
                    </Section>
                    <details className="overflow-hidden rounded-xl border border-stone-200 bg-white">
                      <summary className="cursor-pointer px-4 py-3 text-sm font-medium text-stone-800 hover:bg-stone-50">
                        Details
                      </summary>
                      <div className="grid gap-4 border-t border-stone-200 bg-stone-50/40 p-4">
                        <div className="grid gap-4 md:grid-cols-2">
                          <Section eyebrow="Verification" title="Freshness">
                            <div className="grid gap-2 text-sm">
                              <p className="flex justify-between"><span className="text-stone-500">Last verified</span><span>{formatDate(detail.last_verified_at || detail.approved_at)}</span></p>
                              <p className="flex justify-between"><span className="text-stone-500">Source</span><span>{detail.source_status || 'not labeled'}</span></p>
                              <p className="flex justify-between"><span className="text-stone-500">Freshness</span><span>{labelize(cardForm.freshness_risk || 'unknown')}</span></p>
                            </div>
                          </Section>
                          <Section eyebrow="Trust" title="Confidence">
                            <SelectField label="Confidence" value={cardForm.confidence || ''} options={['not_assessed', 'low', 'medium', 'high']} onChange={value => setCardForm(f => ({ ...f, confidence: value }))} disabled={publishedCardLocked} />
                          </Section>
                        </div>
                        <Section title="Status and internal notes">
                          <div className="mb-4 flex flex-wrap gap-2">
                            <Badge kind="accent">Editorial: {labelize(detail.admin_review_status)}</Badge>
                            <Badge>{`Publish: ${labelize(detail.publish_status)}`}</Badge>
                            <Badge kind={detail.agent_card_status === 'ready_for_agent' ? 'success' : 'neutral'}>Agent-ready: computed from publish</Badge>
                          </div>
                          {(detail.action_result_note || detail.action_requested_at || detail.action_error) && (
                            <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50/50 p-3 text-sm text-stone-700">
                              {detail.action_result_note && <p>{detail.action_result_note}</p>}
                              {detail.action_requested_at && <p className="mt-1 text-xs text-stone-500">Queued {formatDate(detail.action_requested_at)}</p>}
                              {detail.action_error && <p className="mt-1 text-xs text-red-700">{detail.action_error}</p>}
                            </div>
                          )}
                          <TextArea label="Internal notes" rows={4} value={cardForm.admin_notes} onChange={value => setCardForm(f => ({ ...f, admin_notes: value }))} placeholder="Admin-only. Not guest-facing and not Composer guidance." focusKey="admin-notes" highlight={focusField === 'admin-notes'} disabled={publishedCardLocked} />
                        </Section>
                      </div>
                    </details>
                  </div>
                )}

                {activeTab === 'versions' && (
                  <VersionHistory
                    versions={detail.versions || []}
                    publishedVersionId={detail.published_item?.published_card_version_id}
                  />
                )}

                  </div>
                </div>
              </div>
            </>
          )}
          {saveNotice && (
            <div role="status" aria-live="polite" className="absolute bottom-5 right-5 z-50 flex items-center gap-2 rounded-lg border border-green-200 bg-white px-3 py-2 text-sm font-medium text-green-800 shadow-lg">
              <CheckCircle2 className="h-4 w-4" />
              {saveNotice}
            </div>
          )}
        </section>
      </div>
      {showAddPlace && (
        <AddPlaceModal
          tags={tags}
          vocab={vocab}
          onClose={() => setShowAddPlace(false)}
          onCreated={handlePlaceCreated}
        />
      )}
      {showImportCsv && (
        <ImportPlacesModal
          onClose={() => setShowImportCsv(false)}
          onImported={handleCsvImported}
        />
      )}
    </div>
  );
}
